import {useEffect, useState} from 'react';
import {User as FirebaseUser} from 'firebase/auth';
import {auth} from '../../firebaseApp.ts';
import FormError from '../../pages/login/FormError.tsx';
import {
    getSsoProviderStatuses,
    linkGoogleAccount,
    linkMicrosoftAccount,
    SsoProviderId,
    SsoProviderStatus,
    unlinkSsoProvider,
} from '../../lib/ssoAuth.ts';

function ProviderIcon({providerId}: { providerId: SsoProviderId }) {
    if (providerId === 'google.com') {
        return <img src="/google.png" alt="" className="h-5 w-5" aria-hidden="true"/>;
    }

    return <img src="/microsoft.png" alt="" className="h-5 w-5" aria-hidden="true"/>;
}

export function SsoAccountLinkingSection() {
    const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(auth.currentUser);
    const [providerStatuses, setProviderStatuses] = useState<SsoProviderStatus[]>([]);
    const [busyProviderId, setBusyProviderId] = useState<SsoProviderId | null>(null);
    const [formError, setFormError] = useState<unknown>('');

    useEffect(() => {
        return auth.onAuthStateChanged((user) => {
            setFirebaseUser(user);
            if (user) {
                setProviderStatuses(getSsoProviderStatuses(user));
            } else {
                setProviderStatuses([]);
            }
        });
    }, []);

    function refreshProviderStatuses(user: FirebaseUser) {
        setFirebaseUser(user);
        setProviderStatuses(getSsoProviderStatuses(user));
    }

    async function linkProvider(providerId: SsoProviderId) {
        setBusyProviderId(providerId);
        setFormError('');

        try {
            const user = providerId === 'google.com'
                ? await linkGoogleAccount()
                : await linkMicrosoftAccount();
            refreshProviderStatuses(user);
        } catch (error) {
            setFormError(error);
        } finally {
            setBusyProviderId(null);
        }
    }

    async function unlinkProvider(providerId: SsoProviderId) {
        setBusyProviderId(providerId);
        setFormError('');

        try {
            const user = await unlinkSsoProvider(providerId);
            refreshProviderStatuses(user);
        } catch (error) {
            setFormError(error);
        } finally {
            setBusyProviderId(null);
        }
    }

    if (!firebaseUser) {
        return null;
    }

    return (
        <div className="space-y-3">
            <h3 className="text-xl font-gray-900 py-3 pt-6">Inlogproviders koppelen</h3>
            <p className="text-sm text-gray-600">
                Koppel een provider om later zonder wachtwoord in te loggen. Je kunt alleen providers koppelen die hetzelfde e-mailadres gebruiken als je PocketCFO-account.
            </p>

            <dl className="space-y-3 text-sm leading-6">
                {providerStatuses.map((provider) => (
                    <div key={provider.id} className="justify-between bg-gray-50 p-3 rounded-md sm:flex">
                        <dt className="font-medium text-gray-900 sm:w-64 sm:pr-6 flex items-center gap-2">
                            <ProviderIcon providerId={provider.id}/>
                            {provider.label}
                        </dt>
                        <dd className="mt-1 flex items-center justify-between gap-x-6 sm:mt-0">
                            <span className="text-gray-900">
                                {provider.linked ? 'Gekoppeld' : 'Niet gekoppeld'}
                            </span>
                            {provider.linked ? (
                                provider.canUnlink && (
                                    <button
                                        type="button"
                                        disabled={busyProviderId !== null}
                                        onClick={() => unlinkProvider(provider.id)}
                                        className="font-semibold text-primary hover:opacity-70 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        {busyProviderId === provider.id ? 'Bezig...' : 'Loskoppelen'}
                                    </button>
                                )
                            ) : (
                                <button
                                    type="button"
                                    disabled={busyProviderId !== null}
                                    onClick={() => linkProvider(provider.id)}
                                    className="font-semibold text-primary hover:opacity-70 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    {busyProviderId === provider.id ? 'Bezig...' : 'Koppelen'}
                                </button>
                            )}
                        </dd>
                    </div>
                ))}
            </dl>

            {formError && <FormError message={formError}/>}
        </div>
    );
}
